import { FC, useEffect, useMemo, useState } from 'react'

import { type TotalViews as TotalViewsType } from '@img-arena/consoles-api-streaming'
import { useTheme } from '@mui/material'
import { LineChart } from '@mui/x-charts'
import { streamingRestApi } from '@/service/streamingApi'

import { StatisticsCard, StatisticsCardSkeleton } from '../../StatisticsCard'
import { makeYAxisData } from '../../utils'
import { STATISTICS_CSV_HEADERS } from '../constants'
import { ComponentWithStatisticsQueryParams } from '../types'

export const TOTAL_VIEWS_CHART_TEST_ID = 'total-views-line-chart' as const
export const TOTAL_VIEWS_CARD_TEST_ID = 'total-views-card' as const
export const TOTAL_VIEWS_SKELETON_TEST_ID = 'total-views-skeleton' as const

export const TotalViews: FC<ComponentWithStatisticsQueryParams> = ({ queryParams }) => {
  const analyticsEndpoints = streamingRestApi.endpoints.analytics
  const [totalViews, setTotalViews] = useState<TotalViewsType['views'] | null>(null)
  const theme = useTheme()

  useEffect(() => {
    const ac = new AbortController()

    analyticsEndpoints.getTotalViews(queryParams, ac.signal).then((data) => {
      setTotalViews(data.views)
    })

    return () => {
      ac.abort()
    }
  }, [...Object.values(queryParams)])

  const csvData = useMemo(() => {
    if (!totalViews?.length) return

    const csvHeaders = [STATISTICS_CSV_HEADERS.PERIOD_START, STATISTICS_CSV_HEADERS.TOTAL_VIEWS]
    const data = totalViews.map(({ periodStart, totalViews }) => ({
      [STATISTICS_CSV_HEADERS.PERIOD_START]: periodStart,
      [STATISTICS_CSV_HEADERS.TOTAL_VIEWS]: totalViews
    }))

    return {
      csvHeaders,
      data
    }
  }, [totalViews])

  if (!totalViews) {
    return <StatisticsCardSkeleton testId={TOTAL_VIEWS_SKELETON_TEST_ID} height={600} />
  }

  const yAxisData = makeYAxisData(Math.max(...totalViews.map((item) => item.totalViews)))

  return (
    <StatisticsCard title="Daily Views" testId={TOTAL_VIEWS_CARD_TEST_ID} csvData={csvData}>
      <LineChart
        data-testid={TOTAL_VIEWS_CHART_TEST_ID}
        margin={{ right: 50 }}
        grid={{ vertical: true, horizontal: true }}
        sx={theme.palette.charts.lineChart.component}
        xAxis={[
          {
            dataKey: 'periodStart',
            scaleType: 'point',
            valueFormatter: (value) => {
              return (value && new Date(value.toString()).toLocaleDateString()) || ''
            }
          }
        ]}
        yAxis={[
          {
            width: 100,
            dataKey: 'totalViews',
            data: yAxisData,
            valueFormatter: (value) => {
              return (value && value.toLocaleString()) || ''
            },
            scaleType: 'linear',
            colorMap: {
              type: 'continuous',
              min: 0,
              max: Math.max(...yAxisData),
              color: theme.palette.charts.lineChart.yAxis.color
            }
          }
        ]}
        series={[
          {
            dataKey: 'totalViews',
            data: totalViews.map((item) => item.totalViews),
            label: 'Total Views',
            area: true
          }
        ]}
        dataset={totalViews}
        height={600}
        hideLegend
      />
    </StatisticsCard>
  )
}
